import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTour, TourType } from '../hooks/useTour';
import '../styles/tour.css';

interface HelpTourButtonProps {
  className?: string;
}

/**
 * Help Tour Button
 * 
 * A "?" button that appears in the header next to IntentModeSwitcher.
 * Allows users to start/replay guided tours for the current page.
 */
export const HelpTourButton: React.FC<HelpTourButtonProps> = ({ className = '' }) => {
  const location = useLocation();
  const { replayTour } = useTour();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Determine which tours are available based on current route
  const getAvailableTours = (): { label: string; tourType: TourType; icon: string }[] => {
    const path = location.pathname;
    
    if (path.includes('/generator')) {
      // On generator page - check which tab/mode is active
      const searchParams = new URLSearchParams(location.search);
      const tab = searchParams.get('tab');
      
      if (tab === 'wallet' || path.includes('wallet')) {
        return [
          { label: 'Wallet Pass Tour', tourType: 'wallet_generic', icon: '📱' },
        ];
      }
      
      // Default generator tours
      return [
        { label: 'Email Signature Tour', tourType: 'signature', icon: '✉️' },
        { label: 'Digital Card Tour', tourType: 'card', icon: '💳' },
        { label: 'Wallet Pass Tour', tourType: 'wallet_generic', icon: '📱' },
      ];
    }
    
    if (path.includes('/create-pass') || path.includes('/wallet')) {
      return [
        { label: 'Wallet Pass Tour', tourType: 'wallet_generic', icon: '📱' },
      ];
    }
    
    // Default - show all available tours
    return [
      { label: 'Email Signature Tour', tourType: 'signature', icon: '✉️' },
      { label: 'Digital Card Tour', tourType: 'card', icon: '💳' },
      { label: 'Wallet Pass Tour', tourType: 'wallet_generic', icon: '📱' },
    ];
  };
  
  const availableTours = getAvailableTours();
  
  const handleStartTour = (tourType: TourType) => {
    setIsOpen(false);
    // Small delay to let dropdown close before starting tour
    setTimeout(() => {
      replayTour(tourType);
    }, 100);
  };
  
  // If no tours available, don't render
  if (availableTours.length === 0) {
    return null;
  }
  
  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Help Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-[#e11d48]/10 hover:text-[#e11d48] 
          flex items-center justify-center transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-[#e11d48] focus:ring-offset-2"
        title="Start guided tour"
        aria-label="Help and guided tours"
      >
        <span className="text-sm font-semibold text-gray-600 hover:text-[#e11d48]">?</span>
      </button>
      
      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-56 bg-white/95 backdrop-blur-md rounded-xl 
          shadow-xl border border-gray-100/50 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-2">
            <p className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#e11d48]">
              Guided Tours
            </p>
            
            {availableTours.map((tour) => (
              <button
                key={tour.tourType}
                onClick={() => handleStartTour(tour.tourType)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left
                  transition-all hover:bg-[#e11d48]/5 hover:text-[#e11d48] text-gray-700"
              >
                <span className="text-lg">{tour.icon}</span>
                <span className="font-medium text-sm">{tour.label}</span>
              </button>
            ))}
            
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="px-3 py-1.5 text-xs text-gray-400">
                Click to replay any tour
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpTourButton;
